import React from "react";
import { ChangeEvent, FormEvent } from "react";
import ReactModal from "react-modal";
import { Prompt } from "react-router";
import { RouteComponentPropsHistory } from "../../types";

import { V2 } from "../../Api";
import withTitle from "../withTitle";

import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ValidateInput from "../ValidateInput";
import ViewToolbar from "../ViewToolbar";

import "../../blocks.css";
import "../../modal.css";
import "../../schedule.css";
import "../viewer.css";

interface IBonusCreationViewState {
    changedFields: string[];
    hasChanged: boolean;
    isEdit: boolean;
    isLoading: boolean | string;
    isModalOpen: boolean;
    isEditingBrackets: boolean;
    submitStatus: boolean | string;

    name: string;
    unit: string;
    value: string;
    value2?: string;
    extra?: string;
    extraOptions?: any[];
    conditionalModifier: string;
    brackets: any[];
    bracketNewAmount: string;
    bracketNewValue: string;

    groupId: string;
    type: number;
}

class BonusCreationView extends React.Component<RouteComponentPropsHistory<any, any>, IBonusCreationViewState> {
    private inputs: ValidateInput[];

    constructor(props: Readonly<RouteComponentPropsHistory<any, any>>) {
        super(props);

        this.state = {
            changedFields: [],
            hasChanged: false,
            isEdit: props.match.params.id !== undefined,
            isLoading: !!this.props.match.params.id,
            isModalOpen: false,
            isEditingBrackets: false,
            submitStatus: false,

            name: "",
            unit: "1",
            value: "0",
            conditionalModifier: "1.000",
            brackets: [],
            bracketNewAmount: "",
            bracketNewValue: "",

            groupId: (props.location.state && props.location.state.groupId) || "",
            type: (props.location.state && props.location.state.type) || 0,
        };

        this.inputs = [];

        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.cancelForm = this.cancelForm.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.doRealDelete = this.doRealDelete.bind(this);
        this.closeDialog = this.closeDialog.bind(this);
        this.setTitleAppointment = this.setTitleAppointment.bind(this);
        this.addNewBracket = this.addNewBracket.bind(this);
        this.deleteBracket = this.deleteBracket.bind(this);
        this.handleBracketUpdate = this.handleBracketUpdate.bind(this);
    }

    public async componentDidMount() {
        if (!this.props.match.params.id) {
            // New item
            if (this.state.type === 1) {
                // Office appointments
                const apptTypes = await V2.get<any[]>("appointments/types");
                this.setState({
                    extraOptions: apptTypes
                });
            }
            return;
        }

        try {
            const bonus = await V2.get<any>(`bonus/${this.props.match.params.id}`);
            let extraOptions = undefined;

            if (bonus.type === 1) {
                // Office appointments
                const apptTypes = await V2.get<any[]>("appointments/types");
                extraOptions = apptTypes;
            }

            this.setState({
                isLoading: false,

                name: bonus.name,

                unit: bonus.brackets && bonus.unit === 1
                    ? "11"
                    : bonus.unit,
                value: bonus.value,
                value2: bonus.value2,
                conditionalModifier: bonus.conditionalModifier,

                groupId: bonus.groupId,
                type: bonus.type,
                extra: bonus.extra,
                extraOptions,
                brackets: bonus.brackets?.sort((a: any, b: any) => +a.amount - +b.amount),
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        if (typeof this.state.isLoading === "string") {
            return <div className="error-box">{this.state.isLoading}</div>;
        }

        if (this.state.isLoading) {
            return <div className="spinner spinner-center lds-dual-ring-big" />
        }

        if (this.state.type === -1 || !this.state.groupId) {
            return <div className="error-text">Unknown type or group</div>;
        }

        return (
            <div className="viewer-root with-actionbar">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem condition={this.state.isEdit}>
                            <button type="button" onClick={this.handleDelete}><i className="icon-trash" /><span>Delete Bonus Type</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ReactModal
                    isOpen={this.state.isModalOpen} onRequestClose={this.closeDialog}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open"
                    contentLabel="Confirm Delete">
                    <form className="input-form" onSubmit={this.doRealDelete}>
                        <section className="info-block">
                            <header>
                                <h3>Confirm Delete</h3>
                            </header>

                            <span>Are you sure you want to delete this bonus type?</span>
                            <p>{this.state.name}</p>
                            <div className="action-panel-dialog">
                                <button type="submit" className="button red push-right">Yes, Delete</button>
                                <button type="button" className="button" onClick={this.closeDialog}>No</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                <form className="input-form" onSubmit={this.submitForm}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Basic Information</h3>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name" className="required">Name</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name" name="name" value={this.state.name} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-unit" className="required">Unit</label>
                                    <select id="frm-unit" name="unit" value={this.state.unit} onChange={this.formInputChanged} autoComplete="off_please_chrome">
                                        <option value="0">Points</option>
                                        <option value="1">Currency</option>
                                        <option value="11">Currency Scale</option>
                                    </select>
                                </div>
                                <div className={"input-form-group" + (this.state.unit === "11" ? " hidden" : "")}>
                                    <label htmlFor="frm-value" className="required">{this.state.type === 1 || this.state.type === 0 ? "Private insurance value" : "Value"}</label>
                                    <ValidateInput type="number" ref={this.receiveValidationRef} id="frm-value" name="value" value={this.state.value} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>
                            {this.state.type === 1
                                ? <>
                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-appt-type" className="required">Appointment Type</label>
                                            <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-appt-type" name="extra" value={this.state.extra} onChange={e => this.formInputChanged(e as ChangeEvent<HTMLInputElement>, undefined, this.setTitleAppointment)} autoComplete="off_please_chrome" isRequired={true}>
                                                <option />
                                                {this.state.extraOptions?.map(a => <option key={a.id} value={a.type}>{a.name}</option>)}
                                            </ValidateInput>
                                        </div>
                                    </section>
                                </>
                                : null}
                            {this.state.type === 1 || this.state.type === 0
                                ? <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-modifier" className="required">New patient multiplier</label>
                                        <ValidateInput type="number" ref={this.receiveValidationRef} id="frm-modifier" name="conditionalModifier" value={this.state.conditionalModifier} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                    </div>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-value2" className="required">Non-private insurance value</label>
                                        <ValidateInput type="number" ref={this.receiveValidationRef} id="frm-value2" name="value2" value={this.state.value2} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                    </div>
                                </section>
                                : null}
                        </section>
                        {this.state.unit === "11"
                            ? <div className="section-bare">
                                <table className="section-table table-vertical wrap table-edit">
                                    <thead>
                                        <tr>
                                            <th>Cases</th>
                                            <th className="col-smallest">Dollars</th>
                                            <th className="col-smallest" />
                                        </tr>
                                    </thead>
                                    <thead className="table-vertical-head-small">
                                        <tr>
                                            <th>Currency Scale Brackets</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.brackets.map((a, i) => <tr key={i}>
                                            <td data-caption="Cases: "><input type="number" value={a.amount} onChange={e => this.handleBracketUpdate(a, "amount", e.currentTarget.value)} /></td>
                                            <td data-caption="Dollars: "><input type="number" value={a.value} onChange={e => this.handleBracketUpdate(a, "value", e.currentTarget.value)} /></td>
                                            <td><button type="button" className="button red small icon-times" onClick={() => this.deleteBracket(i)} /></td>
                                        </tr>)}
                                        <tr>
                                            <td data-caption="Cases: "><input type="number" name="bracketNewAmount" value={this.state.bracketNewAmount} onChange={this.formInputChanged} placeholder="Cases" /></td>
                                            <td data-caption="Dollars: "><input type="number" name="bracketNewValue" value={this.state.bracketNewValue} onChange={this.formInputChanged} placeholder="Dollars" /></td>
                                            <td><button type="button" className="button small icon-plus" onClick={this.addNewBracket} /></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            : <section className="block-spacer" />
                        }
                    </div>
                    &nbsp;
                    {
                        this.state.submitStatus === true
                            ? <div className="spinner lds-dual-ring" />
                            : typeof this.state.submitStatus === "string"
                                ? <span className="error-text">{this.state.submitStatus}</span>
                                : ""
                    }
                    <div className="action-panel">
                        <button type="submit" className="button push-right" onClick={this.submitForm} disabled={this.state.submitStatus === true}>Submit</button>
                        <button type="button" className="button" onClick={this.cancelForm} disabled={this.state.submitStatus === true}>Cancel</button>
                    </div>
                </form>

            </div>
        );
    }

    private receiveValidationRef(input: ValidateInput) {
        this.inputs.push(input);
    }

    private async submitForm(e: FormEvent) {
        e.preventDefault();

        let isAllValid = true;
        for (const input of this.inputs) {
            isAllValid = input.validate() && isAllValid;
        }

        if (!isAllValid) {
            return;
        }

        // Unit "11" is a currency scale
        const unit = this.state.unit === "11" ? 1 : this.state.unit;

        const data = {
            name: this.state.name,
            unit: unit,
            value: this.state.value,

            groupId: this.state.groupId,
            type: this.state.type,
            extra: this.state.extra,
            value2: this.state.value2,
            conditionalModifier: this.state.conditionalModifier,

            brackets: this.state.unit === "11" ? this.state.brackets : undefined
        };

        try {
            this.setState({
                submitStatus: true
            });

            if (this.state.isEdit) {
                await V2.patch(`bonus/${this.props.match.params.id}`, data);
            }
            else {
                await V2.post("bonus", data);
            }

            this.setState({
                hasChanged: false,
                submitStatus: false
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                submitStatus: e.message
            });
        }
    }

    private cancelForm() {
        this.props.history.goBack();
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>, preCallback?: (state: Partial<IBonusCreationViewState>) => void, postCallback?: (state: Partial<IBonusCreationViewState>) => void) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState(s => {
            if (!s.changedFields.some(a => a === name)) {
                s.changedFields.push(name);
            }

            const newState = {
                changedFields: s.changedFields,
                hasChanged: true,
                [name]: value
            }

            if (preCallback) {
                preCallback(newState);
            }

            return newState as any;
        }, () => postCallback?.(this.state));
    }

    private handleBracketUpdate(bracket: any, property: string, value: string) {
        const brackets = this.state.brackets;

        bracket[property] = value;

        this.setState({
            hasChanged: true,
            brackets
        });
    }

    private handleDelete() {
        this.setState({
            isModalOpen: true
        });
    }

    private async doRealDelete() {
        try {
            this.setState({
                isModalOpen: false,
                submitStatus: true,
            });

            await V2.delete(`bonus/${this.props.match.params.id}`);

            this.setState({
                hasChanged: false,
                submitStatus: false
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                submitStatus: e.message
            });
        }
    }

    private closeDialog() {
        this.setState({
            isModalOpen: false
        });
    }

    private setTitleAppointment() {
        if (this.state.extra) {
            const appointmentType = this.state.extraOptions?.find(a => a.type === this.state.extra);
            if (appointmentType) {
                this.setState({
                    name: appointmentType.name
                });
            }
        }
    }

    private addNewBracket() {
        const brackets = this.state.brackets;

        brackets.push({
            amount: this.state.bracketNewAmount,
            value: this.state.bracketNewValue,
        });

        this.setState({
            brackets,
            bracketNewAmount: "",
            bracketNewValue: "",
        });
    }

    private deleteBracket(index: number) {
        const brackets = this.state.brackets;

        brackets.splice(index, 1);

        this.setState({
            brackets
        });
    }
}

export default withTitle("Add/Edit Bonus Type", BonusCreationView);
